import { Data2, useDataTypesHooks } from "../../../features/data-types"
import { useFileActions } from "../../../features/files"
import { TableName } from "../../../tables"
import { Currency } from "../../../tables/types/types"
import { useTicketActions } from "../actions/ticketActions_NEW"
import { useTaxBuilders } from "../builders/taxBuilders"
import { useTicketBuilders } from "../builders/ticketBuilders"
import { useTicketLoaders } from "../loaders/ticketLoaders"
import { Ticket } from "../state/types"

export const useTicketService = () => {
    const { generateCode } = useDataTypesHooks()

    const { buildTicket } = useTicketBuilders()
    const { buildTaxItem } = useTaxBuilders()

    const ticketLoaders = useTicketLoaders()

    const ticketActions = useTicketActions()
    const { saveFile, removeFile } = useFileActions()

    const createTicket = async (
        ticketData: Data2,
        fileSubPath: (ticket: Ticket) => string,
        taxItemsData: Data2[]
    ) => {
        const code = await generateCode(TableName.TICKETS)
        const taxItems = taxItemsData.map(buildTaxItem)
        const newTicketData: Data2 = {
            ...ticketData,
            code,
            taxItems,
            payedAmount: 0,
            currency: Currency.ARS
        }

        const ticket = buildTicket(newTicketData)
        
        const { ticketFile } = ticketData
        if (ticketFile) {
            const file = ticketFile as File
            const ticketUrl = await saveFile(fileSubPath(ticket), file)
            ticket.ticketUrl = ticketUrl
        }
        
        return ticketActions.saveTicket(ticket)
    }

    const updateTicket = async (
        ticketData: Data2,
        fileSubPath: (ticket: Ticket) => string,
        taxItemsData?: Data2[],
    ) => {
        const { loadTicket } = ticketLoaders()
        const loadedTicket = await loadTicket(ticketData.id! as string)

        const taxItems = taxItemsData ? taxItemsData.map(buildTaxItem) : loadedTicket!.taxItems
        const newTicketData: Data2 = {
            ...loadedTicket!,
            ...ticketData,
            taxItems
        }
        
        const ticket = buildTicket(newTicketData)

        const { ticketFile } = ticketData
        if (ticketFile) {
            const file = ticketFile as File
            const ticketUrl = await saveFile(fileSubPath(ticket), file)
            ticket.ticketUrl = ticketUrl
        } else {
            delete ticket.ticketUrl
        }

        return ticketActions.saveTicket(ticket)
    }

    const uploadTicketFile = async (
        ticketId: string,
        fileSubPath: (ticket: Ticket) => string,
        ticketFile: File
    ) => {
        const { loadTicket } = ticketLoaders()
        const loadedTicket = await loadTicket(ticketId, true)

        const ticketUrl = await saveFile(fileSubPath(loadedTicket!), ticketFile)

        const ticket = buildTicket({
            ...loadedTicket,
            ticketUrl
        })

        return ticketActions.saveTicket(ticket)
    }

    const removeTicket = async (id: string) => {
        const { loadTicket } = ticketLoaders()
        const loadedTicket = await loadTicket(id)
        if (loadedTicket?.ticketUrl) {
            await removeFile(loadedTicket.ticketUrl)
        }
        
        await ticketActions.removeTicket(id)
    }

    return {
        createTicket,
        updateTicket,
        uploadTicketFile,
        removeTicket
    }
}
